// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import { Image, StyleSheet, TextInput } from 'react-native';
import { Redirect, useRootNavigationState, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useI18n } from '@/i18n';
import { useUser } from './user-context';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { resolveTourByInviteCode } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';

const START_HERO_IMAGE_OPTIONS = {
  width: 1200,
  height: 474,
  bucketStep: 1,
} as const;

export default function Index() {
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const loginHeroImage = useMemo(
    () => getAssetImageSource('images/login-hero.png', '', '/', START_HERO_IMAGE_OPTIONS),
    [],
  );
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [joiningWithCode, setJoiningWithCode] = useState(false);
  const { handleRedirect } = useAuthRedirect({ user, setUser });

  const handleJoinWithCode = async () => {
    const normalizedCode = inviteCode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase();
    if (!normalizedCode) {
      setInviteCodeError(t('start.roundCodeRequired'));
      return;
    }

    setInviteCodeError(null);
    setJoiningWithCode(true);
    try {
      const resolvedInvite = await resolveTourByInviteCode({
        apiBaseUrl,
        inviteToken: normalizedCode,
      });

      router.push({
        pathname: '/tour/invite/[inviteToken]',
        params: {
          inviteToken: resolvedInvite.inviteToken,
          tourId: resolvedInvite.tourId,
          targetType: resolvedInvite.targetType ?? 'tour',
          roundId: resolvedInvite.roundId,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setInviteCodeError(
        message.includes('invalidInviteToken')
          ? t('start.roundCodeInvalid')
          : t('start.roundCodeLookupFailed')
      );
    } finally {
      setJoiningWithCode(false);
    }
  };

  if (!isNavigationReady) {
    return null;
  }

  if (user) {
    return <Redirect href="/welcome" />;
  }

  return (
    <View style={styles.container}>
      <Image source={loginHeroImage} style={styles.hero} resizeMode="contain" />
      <Text style={styles.title}>{t('start.title')}</Text>
      <Text style={styles.subtitle}>{t('start.subtitle')}</Text>
      <LanguageSwitcher />

      <View style={styles.card}>
        <Text style={styles.fieldLabel}>{t('start.roundCodeLabel')}</Text>
        <TextInput
          value={inviteCode}
          onChangeText={(next) => {
            setInviteCode(next);
            if (inviteCodeError) {
              setInviteCodeError(null);
            }
          }}
          placeholder={t('start.roundCodePlaceholder')}
          placeholderTextColor="#8FA08B"
          autoCapitalize="characters"
          autoCorrect={false}
          style={styles.input}
          onSubmitEditing={handleJoinWithCode}
          returnKeyType="go"
        />
        {inviteCodeError ? <Text style={styles.errorText}>{inviteCodeError}</Text> : null}
        <GoffyButton
          label={t('start.joinWithCodeCta')}
          onPress={() => {
            void handleJoinWithCode();
          }}
          disabled={joiningWithCode}
          tone="dark"
          style={styles.button}
        />
        <GoffyButton
          label={t('start.signUpCta')}
          onPress={() => router.push('/register')}
          tone="dark"
          style={styles.button}
        />
        <GoffyButton
          label={t('start.logInCta')}
          onPress={() => {
            void handleRedirect();
          }}
          tone="accent"
          style={styles.button}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    gap: 16,
    justifyContent: 'center',
    backgroundColor: '#011806',
  },
  hero: {
    width: '100%',
    maxWidth: 720,
    aspectRatio: 1200 / 474,
    alignSelf: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    textAlign: 'center',
    color: appColors.text.primary,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    color: '#A1B49E',
    maxWidth: 420,
    alignSelf: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 460,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    padding: 16,
    gap: 10,
  },
  fieldLabel: {
    fontSize: 12,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
    fontWeight: '700',
    color: '#8FA08B',
  },
  input: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    backgroundColor: '#0C2510',
  },
  errorText: {
    color: '#FF7A7A',
    fontSize: 13,
  },
  button: {
    width: '100%',
  },
});
